import { BaseProjectProps } from "@custom-types/project-types";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useProjectArchiveDialog } from "@hooks/projects/use-project-archive-dialog";
import { appStateSelector } from "@store/app/app-selector";
import {
  isProjectActiveSelector,
  isProjectArchivingAllowedSelector,
  isProjectUnarchivingAllowedSelector,
} from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import {
  isProjectDownloaded,
  isProjectReadyForVaultDownload,
  openInSphereVault as openProjectInSphereVault,
} from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";

interface UseProjectActionsReturn {
  /** Function to archive or unarchive the project. Returns true if successful, false otherwise */
  archiveOrUnarchiveProject: () => Promise<boolean>;

  /** Flag indicating if the project is currently active */
  isProjectActive: boolean;

  /** Flag indicating if the user is allowed to archive or unarchive the project */
  isAllowedToArchiveOrUnarchive: boolean;

  /** Flag indicating if the archive action is disabled due to insufficient permissions or project state */
  isArchiveActionDisabled: boolean;

  /** Flag indicating if the user can delete the project */
  canDeleteProject: boolean;

  /** Label for the archive action: "Archive" for active projects, "Unarchive" for archived projects */
  archiveActionLabel: string;

  /**
   * Whether the "Open in Sphere Vault" download action should be offered for this
   * project (beta flag enabled AND the project is ready / already downloaded).
   */
  canDownloadFromVault: boolean;

  /** Opens the project in the Sphere Vault downloader in a new tab. */
  openInSphereVault: () => void;
}

/**
 * Hook that provides reusable project actions like share, archive, etc.
 * Can be used in both buttons and menu items
 */
export function useProjectActions(
  project: BaseProjectProps["project"]
): UseProjectActionsReturn {
  const { trackEvent } = useTrackEvent();
  const archiveOrUnarchiveProject = useProjectArchiveDialog({ project });

  // Selectors for project state
  const isProjectActive = useAppSelector(isProjectActiveSelector(project.id));
  const isArchivingAllowed = useAppSelector(
    isProjectArchivingAllowedSelector(project)
  );
  const isUnarchivingAllowed = useAppSelector(
    isProjectUnarchivingAllowedSelector(project)
  );

  // Permission and feature checks
  const { canDeleteProject } = useHasUserValidPermissionProjectLevel({
    project,
  });
  const { canArchiveProject, canUnarchiveProject } =
    useHasValidFeatureProjectLevel();

  // Sphere Vault download is a beta feature, gated behind the beta-testing flag.
  const { isBetaTestingEnabled } = useAppSelector(appStateSelector);

  /**
   * An active project can be archived if the user has the required role.
   * An archived project can be unarchived if the user has the required role and the project has not been downloaded.
   */
  const isAllowedToArchiveOrUnarchive = useMemo(() => {
    return isProjectActive
      ? canArchiveProject
      : canUnarchiveProject && !isProjectDownloaded(project);
  }, [canArchiveProject, canUnarchiveProject, isProjectActive, project]);

  /**
   * Determines if archive/unarchive action is disabled
   */
  const isArchiveActionDisabled = isProjectActive
    ? !isArchivingAllowed
    : !isUnarchivingAllowed;

  /**
   * Get the appropriate label for archive action based on project state
   */
  const archiveActionLabel = isProjectActive ? "Archive" : "Unarchive";

  /**
   * Whether to offer the Sphere Vault download action: only in beta, and only for
   * projects whose export is ready or already downloaded.
   */
  const canDownloadFromVault =
    isBetaTestingEnabled &&
    isProjectReadyForVaultDownload(project.archivingState);

  /**
   * Opens the project in the Sphere Vault downloader (deep-linked by project id)
   * in a new tab.
   */
  function openInSphereVault(): void {
    openProjectInSphereVault({ projectId: project.id, trackEvent });
  }

  return {
    archiveOrUnarchiveProject,
    isProjectActive,
    isAllowedToArchiveOrUnarchive,
    isArchiveActionDisabled,
    canDeleteProject,
    archiveActionLabel,
    canDownloadFromVault,
    openInSphereVault,
  };
}
